import React from 'react';
import { useTranslation } from 'react-i18next';

/**
 * Ready-made rules, ticked one line at a time.
 *
 * A plain multi-select showed the raw keys — `technical_quality,internal_photo` — which tells an
 * administrator nothing about what they are switching on. Each rule gets its own row with what it
 * does underneath, and the whole row is the target: these are four choices, not a text field.
 */
const RuleToggles = ({ value = [], onChange, disabled, widgetProps = {} }) => {
  const { t } = useTranslation();
  const options = widgetProps.options || [];
  const selected = Array.isArray(value) ? value : [];

  const toggle = (key) => onChange(selected.includes(key)
    ? selected.filter(entry => entry !== key)
    : [...selected, key]);

  return (
    <div className="space-y-2">
      {options.map((option) => {
        const checked = selected.includes(option.value);
        return (
          <label
            key={option.value}
            className={[
              'flex cursor-pointer items-start gap-3 rounded-xl border p-3 transition',
              disabled ? 'cursor-not-allowed opacity-60' : '',
              checked
                ? 'border-blue-300 bg-blue-50 dark:border-blue-800 dark:bg-blue-950/40'
                : 'border-gray-200 hover:bg-gray-50 dark:border-gray-700 dark:hover:bg-gray-800'
            ].filter(Boolean).join(' ')}
          >
            <input
              type="checkbox"
              className="mt-0.5 h-4 w-4 shrink-0"
              checked={checked}
              disabled={disabled}
              onChange={() => toggle(option.value)}
            />
            <span className="min-w-0">
              <span className={`block text-sm ${checked
                ? 'font-semibold text-gray-900 dark:text-white'
                : 'font-medium text-gray-700 dark:text-gray-200'}`}
              >
                {t(option.label)}
              </span>
              {option.description && (
                <span className="mt-0.5 block text-xs text-gray-500 dark:text-gray-400">
                  {t(option.description)}
                </span>
              )}
            </span>
          </label>
        );
      })}

      {selected.length === 0 && (
        <p className="text-xs text-gray-500 dark:text-gray-400">
          {t('No standard rule ticked — add one of your own below, or this agent has nothing to look for.')}
        </p>
      )}
    </div>
  );
};

export default RuleToggles;
